"use client";

import React, { createContext, useState, useEffect, useContext } from "react";

interface User {
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const email = params.get("email");
      const name = params.get("name");

      if (email && name) {
        const newUser = { email, name };
        localStorage.setItem("cs_user", JSON.stringify(newUser));
        setUser(newUser);

        // Clean query params
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        const userData = localStorage.getItem("cs_user");
        if (userData) {
          const parsedUser: User = JSON.parse(userData);
          if (parsedUser?.email && parsedUser?.name) {
            setUser(parsedUser);
          }
        }
      }
    } catch (e) {
      console.error("Error reading user from localStorage", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔹 Redirect if not logged in
  useEffect(() => {
    if (!loading && !user) {
      window.location.href = "https://createstudio.app/login?source=collab";
    }
  }, [loading, user]);

  const logout = () => {
    localStorage.removeItem("cs_user");
    setUser(null);
    // also redirect immediately on logout
    window.location.href = "https://createstudio.app/login?source=collab";
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
